'use client'

import type { ReactNode } from 'react'
import Link from 'next/link'
import {
  AlertTriangle,
  Bell,
  Bot,
  CheckCircle2,
  ChevronRight,
  Clock,
  Heart,
  Home,
  LayoutDashboard,
  MessageCircle,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button, ButtonLink } from './Button'
import { Badge } from './Badge'

type Surface = 'light' | 'dark'
type Tone = 'default' | 'emerald' | 'gold' | 'danger' | 'warning' | 'info' | 'muted'

type BaseProps = {
  children?: ReactNode
  className?: string
}

export function SectionContainer({ children, className }: BaseProps) {
  return <section className={cn('premium-section', className)}>{children}</section>
}

export function DashboardSection({ title, eyebrow, action, children, className }: BaseProps & { title: string; eyebrow?: string; action?: ReactNode }) {
  return (
    <section className={cn('premium-dashboard-section', className)}>
      <div className="premium-section-head">
        <div>
          {eyebrow && <span>{eyebrow}</span>}
          <h2>{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

export function SplitLayout({ children, className }: BaseProps) {
  return <div className={cn('premium-split-layout', className)}>{children}</div>
}

export function GridSystem({ children, columns = 3, className }: BaseProps & { columns?: 2 | 3 | 4 }) {
  return <div className={cn('premium-grid-system', `premium-grid-${columns}`, className)}>{children}</div>
}

export function MobileStack({ children, className }: BaseProps) {
  return <div className={cn('premium-mobile-stack', className)}>{children}</div>
}

export function PremiumCard({ children, className, surface = 'light' }: BaseProps & { surface?: Surface }) {
  return <article className={cn('premium-card', `premium-card-${surface}`, className)}>{children}</article>
}

export function IntelligenceCard({ title, description, meta, children, surface = 'dark' }: BaseProps & { title: string; description?: string; meta?: string; surface?: Surface }) {
  return (
    <PremiumCard surface={surface} className="premium-intelligence-card">
      <div className="premium-card-kicker"><Sparkles size={14} /> Intelligence</div>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {children}
      {meta && <small>{meta}</small>}
    </PremiumCard>
  )
}

export function PremiumMetricCard({ label, value, trend, tone = 'emerald' }: { label: string; value: string; trend?: string; tone?: Tone }) {
  return (
    <PremiumCard className="premium-metric-card">
      <span className={cn('premium-dot', `premium-dot-${tone}`)} />
      <strong>{value}</strong>
      <p>{label}</p>
      {trend && <small>{trend}</small>}
    </PremiumCard>
  )
}

export function ActivityCard({ title, description, time, tone = 'emerald' }: { title: string; description: string; time?: string; tone?: Tone }) {
  return (
    <PremiumCard className="premium-activity-card">
      <span className={cn('premium-activity-icon', `premium-activity-${tone}`)}><Clock size={15} /></span>
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
        {time && <small>{time}</small>}
      </div>
    </PremiumCard>
  )
}

export function CampaignCard({ title, raised, target, status }: { title: string; raised: string; target: string; status: string }) {
  return (
    <PremiumCard className="premium-campaign-card">
      <div className="premium-card-row">
        <Badge tone="gold">{status}</Badge>
        <Heart size={16} />
      </div>
      <h3>{title}</h3>
      <div className="premium-progress"><span style={{ width: '68%' }} /></div>
      <p><strong>{raised}</strong> raised of {target}</p>
    </PremiumCard>
  )
}

export function BusinessCard({ name, category, location }: { name: string; category: string; location: string }) {
  return (
    <PremiumCard className="premium-business-card">
      <div className="premium-avatar">{name.charAt(0)}</div>
      <div>
        <h3>{name}</h3>
        <p>{category}</p>
        <small>{location}</small>
      </div>
    </PremiumCard>
  )
}

export function FeedCard({ author, body, action }: { author: string; body: string; action?: ReactNode }) {
  return (
    <PremiumCard className="premium-feed-card">
      <div className="premium-card-row">
        <div className="premium-avatar">{author.charAt(0)}</div>
        <div><strong>{author}</strong><small>Community update</small></div>
      </div>
      <p>{body}</p>
      {action}
    </PremiumCard>
  )
}

export function DashboardCard({ title, value, children }: BaseProps & { title: string; value?: string }) {
  return (
    <PremiumCard className="premium-dashboard-card">
      <div className="premium-card-row">
        <h3>{title}</h3>
        <LayoutDashboard size={16} />
      </div>
      {value && <strong>{value}</strong>}
      {children}
    </PremiumCard>
  )
}

export function PremiumNavbar({ links }: { links: Array<{ label: string; href: string }> }) {
  return (
    <nav className="premium-navbar">
      <Link href="/" className="premium-navbar-brand"><Home size={16} /> Masjids.in</Link>
      <div>
        {links.map((link) => <Link key={link.href} href={link.href}>{link.label}</Link>)}
      </div>
      <ButtonLink href="/onboard" size="sm">Add masjid</ButtonLink>
    </nav>
  )
}

export function SidebarItem({ href, label, active, icon }: { href: string; label: string; active?: boolean; icon?: ReactNode }) {
  return <Link href={href} className={cn('premium-sidebar-item', active && 'is-active')}>{icon || <ChevronRight size={15} />}<span>{label}</span></Link>
}

export function PremiumMobileBottomNav({ items }: { items: Array<{ label: string; href: string; icon?: ReactNode; active?: boolean }> }) {
  return (
    <nav className="premium-mobile-bottom-nav">
      {items.map((item) => <Link key={item.href} href={item.href} className={item.active ? 'is-active' : ''}>{item.icon || <Home size={16} />}<span>{item.label}</span></Link>)}
    </nav>
  )
}

export function Breadcrumb({ items }: { items: Array<{ label: string; href?: string }> }) {
  return (
    <nav className="premium-breadcrumb">
      {items.map((item, index) => item.href ? <Link key={item.label} href={item.href}>{item.label}</Link> : <span key={item.label}>{item.label}</span>).reduce<ReactNode[]>((acc, item, index) => {
        if (index) acc.push(<ChevronRight key={`sep-${index}`} size={13} />)
        acc.push(item)
        return acc
      }, [])}
    </nav>
  )
}

export function SearchInput({ placeholder = 'Search', className }: { placeholder?: string; className?: string }) {
  return <label className={cn('premium-input-wrap', className)}><Search size={16} /><input placeholder={placeholder} /></label>
}

export function AISearchBar({ placeholder = 'Ask Masjids AI to search across the ecosystem' }: { placeholder?: string }) {
  return <label className="premium-ai-search"><Sparkles size={17} /><input placeholder={placeholder} /><Button size="sm" rightIcon={<Send size={14} />}>Ask</Button></label>
}

export function FilterChips({ chips, active }: { chips: string[]; active?: string }) {
  return <div className="premium-filter-chips">{chips.map((chip) => <button key={chip} className={chip === active ? 'is-active' : ''}>{chip}</button>)}</div>
}

export function SelectField({ label, options }: { label: string; options: string[] }) {
  return <label className="premium-field"><span>{label}</span><select>{options.map((option) => <option key={option}>{option}</option>)}</select></label>
}

export function TextareaField({ label, placeholder }: { label: string; placeholder?: string }) {
  return <label className="premium-field"><span>{label}</span><textarea placeholder={placeholder} /></label>
}

export function InlineFormRow({ children }: BaseProps) {
  return <div className="premium-inline-form-row">{children}</div>
}

export function DataTable({ columns, rows }: { columns: string[]; rows: Array<Array<ReactNode>> }) {
  return (
    <div className="premium-table-wrap">
      <table className="premium-table">
        <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
        <tbody>{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  )
}

export function StatsRow({ stats: rowStats }: { stats: Array<{ label: string; value: string }> }) {
  return <div className="premium-stats-row">{rowStats.map((stat) => <MetricPill key={stat.label} label={stat.label} value={stat.value} />)}</div>
}

export function MetricPill({ label, value, tone = 'emerald', icon }: { label: string; value: string; tone?: Tone; icon?: ReactNode }) {
  return (
    <div className={cn('premium-metric-pill', `premium-metric-${tone}`)}>
      {icon ? <span className="premium-metric-ic">{icon}</span> : null}
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  )
}

export function StatusBadge({ children, tone = 'emerald' }: BaseProps & { tone?: Tone }) {
  return <span className={cn('premium-status-badge', `premium-status-${tone}`)}>{children}</span>
}

export function ActivityTimeline({ items }: { items: Array<{ title?: string; description?: string; time?: string } | null | undefined> }) {
  const safeItems = (Array.isArray(items) ? items : [])
    .filter((item): item is { title?: string; description?: string; time?: string } => Boolean(item))

  return (
    <div className="premium-timeline">
      {safeItems.map((item, index) => {
        const title = item.title || 'Live civic update'
        return (
          <div key={`${title}-${index}`}>
            <span />
            <strong>{title}</strong>
            <p>{item.description || 'Operational activity is being refreshed.'}</p>
            {item.time && <small>{item.time}</small>}
          </div>
        )
      })}
    </div>
  )
}

export function AIInsight({ title, body }: { title: string; body: string }) {
  return <IntelligenceCard title={title} description={body} meta="Generated from ecosystem signals" />
}

export function AIRecommendation({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return <PremiumCard className="premium-ai-recommendation"><Sparkles size={18} /><div><h3>{title}</h3><p>{body}</p>{action}</div></PremiumCard>
}

export function AISummary({ children }: BaseProps) {
  return <PremiumCard className="premium-ai-summary"><Bot size={18} /><p>{children}</p></PremiumCard>
}

export function AIAlert({ title, body, tone = 'warning' }: { title: string; body: string; tone?: Tone }) {
  return <div className={cn('premium-ai-alert', `premium-ai-alert-${tone}`)}><AlertTriangle size={17} /><div><strong>{title}</strong><p>{body}</p></div></div>
}

export function AIAssistantDock({ prompt = 'Ask anything about masjids, waqf, donors, or operations.' }: { prompt?: string }) {
  return <div className="premium-ai-dock"><Bot size={18} /><span>{prompt}</span><Button size="sm" variant="ghost">Open</Button></div>
}

export function RiskBadge({ level }: { level: 'low' | 'medium' | 'high' }) {
  const tone = level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'emerald'
  return <StatusBadge tone={tone}>{level} risk</StatusBadge>
}

export function WaqfPropertyCard({ title, location, risk }: { title: string; location: string; risk: 'low' | 'medium' | 'high' }) {
  return <PremiumCard className="premium-waqf-card"><div className="premium-card-row"><ShieldCheck size={18} /><RiskBadge level={risk} /></div><h3>{title}</h3><p>{location}</p></PremiumCard>
}

export function EvidenceCard({ title, status }: { title: string; status: string }) {
  return <PremiumCard className="premium-evidence-card"><CheckCircle2 size={18} /><div><h3>{title}</h3><p>{status}</p></div></PremiumCard>
}

export function LegalStatusCard({ title, status, note }: { title: string; status: string; note?: string }) {
  return <PremiumCard className="premium-legal-card"><StatusBadge tone="info">{status}</StatusBadge><h3>{title}</h3>{note && <p>{note}</p>}</PremiumCard>
}

export function PostComposer() {
  return <PremiumCard className="premium-post-composer"><textarea placeholder="Share an announcement, request, or community update..." /><div><FilterChips chips={['Announcement', 'Event', 'Volunteer']} active="Announcement" /><Button size="sm">Post</Button></div></PremiumCard>
}

export function FeedPost({ author, content }: { author: string; content: string }) {
  return <FeedCard author={author} body={content} action={<InteractionRow likes="24" comments="6" />} />
}

export function InteractionRow({ likes, comments }: { likes: string; comments: string }) {
  return <div className="premium-interaction-row"><button><Heart size={15} /> {likes}</button><button><MessageCircle size={15} /> {comments}</button></div>
}

export function AnnouncementItem({ title, meta }: { title: string; meta: string }) {
  return <div className="premium-announcement-item"><Bell size={17} /><div><strong>{title}</strong><span>{meta}</span></div></div>
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('premium-skeleton', className)} />
}

export function LoadingSurface({ rows = 3 }: { rows?: number }) {
  return <PremiumCard className="premium-loading-surface">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} />)}</PremiumCard>
}

export function TrendLabel({ children }: BaseProps) {
  return <span className="premium-trend-label"><TrendingUp size={13} />{children}</span>
}
